import React, { useEffect } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { MarketingHeader } from '../components/MarketingHeader.js';
import { MarketingFooter } from '../components/MarketingFooter.js';
import { contentEs } from '../data/contentEs.js';
import { contentEn } from '../data/contentEn.js';

const OFFICIAL_ORIGIN = 'https://www.fondadonablanca.com';

function getSeoForPath(pathname: string) {
  const isEn = pathname.startsWith('/en') || pathname.includes('/sitio/en');
  const c = isEn ? contentEn : contentEs;

  if (pathname.includes('/business') || pathname.includes('/empresas')) {
    return {
      title: c.meta.business.title,
      description: c.meta.business.description,
      canonicalPath: isEn ? '/en/business' : '/empresas',
      isEn,
    };
  }
  if (pathname.includes('/locations') || pathname.includes('/sucursales')) {
    return {
      title: c.meta.locations.title,
      description: c.meta.locations.description,
      canonicalPath: isEn ? '/en/locations' : '/sucursales',
      isEn,
    };
  }
  if (pathname.includes('/invoicing') || pathname.includes('/facturacion')) {
    return {
      title: c.meta.invoice.title,
      description: c.meta.invoice.description,
      canonicalPath: isEn ? '/en/invoicing' : '/facturacion',
      isEn,
    };
  }
  if (pathname.includes('/aviso-de-privacidad')) {
    return {
      title: 'Aviso de Privacidad — Doña Blanca',
      description: 'Aviso de Privacidad oficial del restaurante Doña Blanca en cumplimiento con la Ley Federal de Protección de Datos Personales en Posesión de Particulares.',
      canonicalPath: '/aviso-de-privacidad',
      isEn: false,
    };
  }
  return {
    title: c.meta.home.title,
    description: c.meta.home.description,
    canonicalPath: isEn ? '/en' : '/',
    isEn,
  };
}

export const WebsiteLayout: React.FC = () => {
  const location = useLocation();
  const isEn = location.pathname.startsWith('/en') || location.pathname.includes('/sitio/en');

  useEffect(() => {
    const seo = getSeoForPath(location.pathname);

    // 1. Title
    document.title = seo.title;

    // 2. Meta description
    let descTag = document.querySelector('meta[name="description"]') as HTMLMetaElement;
    if (!descTag) {
      descTag = document.createElement('meta');
      descTag.name = 'description';
      document.head.appendChild(descTag);
    }
    descTag.content = seo.description;

    // 3. Canonical URL (pointing to official domain https://www.fondadonablanca.com)
    let canonicalTag = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
    if (!canonicalTag) {
      canonicalTag = document.createElement('link');
      canonicalTag.rel = 'canonical';
      document.head.appendChild(canonicalTag);
    }
    const cleanCanonical = seo.canonicalPath === '/' ? `${OFFICIAL_ORIGIN}/` : `${OFFICIAL_ORIGIN}${seo.canonicalPath}`;
    canonicalTag.href = cleanCanonical;

    // 4. Robots indexing rules:
    // If accessed through staging/temporary Firebase domains (*.web.app, *.firebaseapp.com),
    // mark as noindex to prevent competing with official domain in Google.
    // If accessed through the official domain (fondadonablanca.com / www.fondadonablanca.com),
    // allow full indexing (index, follow).
    let robotsTag = document.querySelector('meta[name="robots"]') as HTMLMetaElement;
    if (!robotsTag) {
      robotsTag = document.createElement('meta');
      robotsTag.name = 'robots';
      document.head.appendChild(robotsTag);
    }

    const host = window.location.hostname.toLowerCase();
    const isStagingHost = host.endsWith('web.app') || host.endsWith('firebaseapp.com') || host === 'localhost' || host === '127.0.0.1';
    robotsTag.content = isStagingHost ? 'noindex, nofollow' : 'index, follow';

    // 5. OpenGraph Tags
    const setMetaTag = (attrName: string, attrVal: string, contentVal: string) => {
      let tag = document.querySelector(`meta[${attrName}="${attrVal}"]`) as HTMLMetaElement;
      if (!tag) {
        tag = document.createElement('meta');
        tag.setAttribute(attrName, attrVal);
        document.head.appendChild(tag);
      }
      tag.content = contentVal;
    };

    setMetaTag('property', 'og:title', seo.title);
    setMetaTag('property', 'og:description', seo.description);
    setMetaTag('property', 'og:url', cleanCanonical);
    setMetaTag('property', 'og:type', 'website');
    setMetaTag('property', 'og:site_name', 'Doña Blanca');
    setMetaTag('property', 'og:locale', seo.isEn ? 'en_US' : 'es_MX');

    // Scroll to top on route navigation (unless hash anchor exists)
    if (!location.hash) {
      window.scrollTo(0, 0);
    } else {
      const element = document.querySelector(location.hash);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  }, [location.pathname, location.hash]);

  return (
    <div className="min-h-screen bg-[#FBF9F5] text-stone-900 font-body flex flex-col selection:bg-[#001557] selection:text-white antialiased">
      {/* Marketing Header */}
      <MarketingHeader isEn={isEn} />

      {/* Main page content */}
      <main className="flex-1">
        <Outlet />
      </main>

      {/* Marketing Footer */}
      <MarketingFooter isEn={isEn} />
    </div>
  );
};
